import { useState, type FormEvent } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { Button, ErrorNote } from '@/components/ui'
import { FullPageMessage } from '@/components/FullPageMessage'
import { AuthLayout } from '@/components/AuthLayout'

export function SignIn() {
  const { session, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState<'password' | 'microsoft' | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (loading) return <FullPageMessage title="Loading" />
  if (session) return <Navigate to="/" replace />

  async function signInWithPassword(event: FormEvent) {
    event.preventDefault()
    setBusy('password')
    setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      // Deliberately the same message for a wrong password and an unknown
      // address: the sign-in page should not confirm who holds an account.
      setError('That email address and password do not match an account.')
      setBusy(null)
    }
  }

  async function signInWithMicrosoft() {
    setBusy('microsoft')
    setError(null)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        // offline_access keeps the session refreshable; the rest is what
        // Entra ID needs to return a name and address.
        scopes: 'openid profile email offline_access',
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) {
      setError('Microsoft sign-in could not start. Try again, or use a password.')
      setBusy(null)
    }
  }

  return (
    <AuthLayout>
      <h1 className="mb-6 text-2xl text-pounamu">Sign in</h1>

      <section className="rounded-lg border border-line bg-surface p-5">
        <h2 className="text-base text-ink">Ki Tua o Matariki team</h2>
        <p className="mb-3 mt-1 text-sm text-inksoft">
          Use your work account. Admin access is issued by the digital lead, not by signing in.
        </p>
        <Button
          variant="secondary"
          className="w-full"
          onClick={signInWithMicrosoft}
          disabled={busy !== null}
        >
          {busy === 'microsoft' ? 'Opening Microsoft…' : 'Continue with Microsoft 365'}
        </Button>
      </section>

      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-inksoft">
        <span className="h-px flex-1 bg-line" />
        Partners and facilitators
        <span className="h-px flex-1 bg-line" />
      </div>

      <form onSubmit={signInWithPassword} className="rounded-lg border border-line bg-surface p-5">
        <label className="block text-sm font-medium text-ink" htmlFor="email">
          Email address
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-sm border border-line px-3 py-2 text-sm"
        />

        <label className="mt-4 block text-sm font-medium text-ink" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-sm border border-line px-3 py-2 text-sm"
        />

        {error && <div className="mt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>}

        <Button type="submit" className="mt-5 w-full" disabled={busy !== null}>
          {busy === 'password' ? 'Signing in…' : 'Sign in'}
        </Button>

        <p className="mt-4 text-sm">
          <Link to="/forgot-password" className="text-pounamu underline underline-offset-4">
            Forgot your password?
          </Link>
        </p>
      </form>

      <p className="mt-6 text-xs text-inksoft">
        Accounts are created by invitation. If your organisation has subscribed and you have not had
        an invitation, ask your organisation admin or contact the Ki Tua o Matariki team.
      </p>
    </AuthLayout>
  )
}
